import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/api';
import { useSocket } from '../../contexts/SocketContext';

interface PersonalWaConnectProps {
  configId: string;
  initialStatus?: string;
  initialError?: string | null;
  onConnected?: () => void;
  onDisconnected?: () => void;
  compact?: boolean;
}

interface PersonalWaEvent {
  type: 'event';
  payload: {
    event: string;
    configId: string;
    data: {
      sessionStatus?: string;
      qrCode?: string;
      phoneNumber?: string;
      pushName?: string;
      isRunning?: boolean;
      reason?: string;
      hint?: string;
      error?: string;
    };
  };
  timestamp: string;
}

const POLL_FALLBACK_MS = parseInt(
  (window as any)?.ENV?.PERSONAL_WA_STATUS_POLL_FALLBACK_MS || '30000',
  10,
);

const PersonalWaConnect: React.FC<PersonalWaConnectProps> = ({
  configId,
  initialStatus = 'disconnected',
  initialError = null,
  onConnected,
  onDisconnected,
  compact = false,
}) => {
  const { socket, isConnected } = useSocket();
  const [status, setStatus] = useState(initialStatus);
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyStatus = (nextStatus: string) => {
    setStatus(nextStatus);
    if (nextStatus === 'ready') {
      setQrImage(null);
      onConnected?.();
    } else if (nextStatus === 'disconnected' || nextStatus === 'failed') {
      onDisconnected?.();
    }
  };

  const fetchStatus = async () => {
    const response = await apiService.get(`/personal-wa/${configId}/status`);
    if (response.data.success) {
      const nextStatus = response.data.data.sessionStatus || 'disconnected';
      applyStatus(nextStatus);
      return nextStatus;
    }
    return status;
  };

  const fetchQr = async () => {
    const response = await apiService.get(`/personal-wa/${configId}/qr`);
    if (response.data.success) {
      setQrImage(response.data.data.qrCode || null);
    }
  };

  const handleConnect = async () => {
    try {
      setLoading(true);
      setError(null);
      await apiService.post(`/personal-wa/${configId}/start`);
      await fetchQr();
      setStatus('qr_ready');
    } catch (err: any) {
      const message =
        err.response?.data?.message ||
        (err.response?.status === 403
          ? 'Access denied. If you are an agent, link your own personal WhatsApp or ask your admin to assign this number to you.'
          : 'Failed to start WhatsApp session');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    try {
      setLoading(true);
      setError(null);
      await apiService.post(`/personal-wa/${configId}/stop`);
      setStatus('disconnected');
      setQrImage(null);
      onDisconnected?.();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to stop WhatsApp session');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setStatus(initialStatus);
    setError(initialError);
  }, [initialStatus, initialError, configId]);

  useEffect(() => {
    if (!configId || !socket) return undefined;

    const handlePersonalWaEvent = (msg: PersonalWaEvent) => {
      if (msg?.type !== 'event' || msg.payload?.configId !== configId) return;

      const { event, data } = msg.payload;

      if (event === 'session.status' && data?.sessionStatus) {
        if (data.sessionStatus === 'disconnected' || data.sessionStatus === 'failed') {
          const message = data.hint || data.error || data.reason;
          if (message && data.reason !== 'manual_stop') {
            setError(message);
          }
        } else if (data.sessionStatus === 'ready') {
          setError(null);
        }
        applyStatus(data.sessionStatus);
      }

      if (event === 'session.qr' && data?.qrCode) {
        setQrImage(data.qrCode);
      }
    };

    const handleSubscribed = (data: { configId: string; sessionStatus?: string }) => {
      if (data.configId !== configId) return;
      if (data.sessionStatus) {
        applyStatus(data.sessionStatus);
      }
    };

    socket.emit('subscribe_personal_wa', { configId });
    socket.on('personal_wa_event', handlePersonalWaEvent);
    socket.on('personal_wa_subscribed', handleSubscribed);

    return () => {
      socket.emit('unsubscribe_personal_wa', { configId });
      socket.off('personal_wa_event', handlePersonalWaEvent);
      socket.off('personal_wa_subscribed', handleSubscribed);
    };
  }, [configId, socket, onConnected, onDisconnected]);

  useEffect(() => {
    if (!configId || isConnected) return undefined;

    let interval: ReturnType<typeof setInterval> | undefined;

    const poll = async () => {
      try {
        const nextStatus = await fetchStatus();
        if (
          nextStatus === 'qr_ready' ||
          nextStatus === 'initializing' ||
          nextStatus === 'authenticating'
        ) {
          await fetchQr();
        }
      } catch (pollError) {
        console.error('Failed to poll personal WhatsApp status', pollError);
      }
    };

    poll();
    interval = setInterval(poll, POLL_FALLBACK_MS);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [configId, isConnected]);

  const statusLabel = status.replace(/_/g, ' ');

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      <div className="flex items-center gap-2">
        <span className={`px-2 py-1 text-xs rounded-full ${
          status === 'ready'
            ? 'bg-green-100 text-green-800'
            : status === 'failed'
              ? 'bg-red-100 text-red-800'
              : 'bg-yellow-100 text-yellow-800'
        }`}>
          {statusLabel}
        </span>
      </div>

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      {qrImage && status !== 'ready' && (
        <div className="flex flex-col items-center gap-2">
          <p className="text-sm text-gray-600">
            Scan this QR code with WhatsApp on your phone (Linked Devices).
          </p>
          <img src={qrImage} alt="WhatsApp QR code" className="max-w-xs rounded border" />
        </div>
      )}

      <div className="flex gap-2">
        {status !== 'ready' ? (
          <button
            type="button"
            onClick={handleConnect}
            disabled={loading}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? 'Starting...' : status === 'qr_ready' ? 'Refresh QR' : 'Connect WhatsApp'}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleStop}
            disabled={loading}
            className="px-4 py-2 border border-yellow-500 text-yellow-700 rounded-md hover:bg-yellow-50 disabled:opacity-50"
          >
            {loading ? 'Stopping...' : 'Disconnect'}
          </button>
        )}
      </div>
    </div>
  );
};

export default PersonalWaConnect;
